import { all, SagaContext } from 'async-saga';
import { routerActions } from 'connected-react-router';
import RecordRTC from 'recordrtc';
import { webrtcSaga as webrtc } from 'webrtc-client-janus';
import { actions } from '..';
import { getBookItToken, getTeamsEnhancedCti, requestOpenBookItWindow, screenRecordingUpload, setDeviceGroupOffice, setDeviceGroupOther, getBookItUrl } from '../../api';
import { saveApplicationSettings } from '../../api/options-control';
import { sendRecordGreetingCommand } from '../../api/voicemail-control';
import { IpcRenderSendClientIntegrated } from '../../Electron/ipcCommands';
import { showConfirmWindow, updateApplicationSetting } from '../../legacy';
import { Identifier } from '../../model';
import { delay } from '../../promise-utils';
import { selectApplicationSetting, selectBookitSettings, selectMyself } from '../reducers/config';
import { selectInTeams } from '../reducers/localSettings';
import { selectExtension } from '../reducers/queueAgents';
import { take, takeEvery } from '../tools/async-saga-types';
import callControl from './call-control';
import commands from './commands';
import contacts from './contacts';
import emails from './emails';
import extensions from './extensions';
import facebook from './facebook';
import instantmessager from './instantmessager';
import janusExtra from './janusExtra';
import media from './media';
import miscApi from './misc-api';
import onInit, { fetchConfig, fetchUnreadVoiceMailCount } from './on-init';
import outbound from './outbound';
import { default as pushUpdates, default as runPushUpdates } from './push-updates';
import queues from './queues';
import recent from './recent';
import search from './search';
import session from './session';
import tasks from './tasks';
import callTranscriptions from './transcriptions';
import twitter from './twitter';
import voiceMail from './voiceMail';
import wrapups from './wrapups';

let streamimg = false;
let recording = false;
let stream: MediaStream | null = null
let recorder: RecordRTC | null = null

async function updateNow(ctx: SagaContext) {
  for (; ;) {
    ctx.dispatch(actions.setNow(Date.now()))
    await delay(1000)
  }
}

const onOpenAgentView = takeEvery(actions.openAgentView, async function (ctx, { mediaId, tab }) {
  let path = '/agent'

  if (mediaId) {
    path += '/' + mediaId

    if (tab) {
      path += '/' + tab
    }
  }

  ctx.dispatch(routerActions.replace(path))
})

const onRecordGreeting = takeEvery(actions.recordGreeting, async function (ctx, { recordingtype, recordingAction, id, server }) {
  const myId = selectMyself(ctx.getState())
  if (myId) {
    if (recordingAction === 'delete') {
      showConfirmWindow('Delete Greeting', 'Are you sure you want to delete this greeting?', async (action: string) => {
        if (action === "yes") {
          sendCommand(myId)
        }
      })
    }
    else {
      sendCommand(myId)
    }
  }

  function sendCommand(myId: Identifier) {
    sendRecordGreetingCommand(recordingtype, recordingAction, id, myId)
  }
})

const setExtensionPresenceExpiry = takeEvery(actions.setExtensionPresenceExpiry, async function (ctx, setting) {
  const state = selectApplicationSetting(ctx.getState(), 'ExtensionPresenceExpiry')
  const myId = selectMyself(ctx.getState())
  if (myId && state) {
    const value = setting ? 'true' : 'false'
    //save to the server
    const s = await saveApplicationSettings(myId, { ...state, value: value })
    //update cache
    ctx.dispatch(actions.updateApplicationSetting(s))
    //update application data on old shit as it's needed
    updateApplicationSetting(s.applicationSettingId, s.settingType, value)
  }
})

const onOpenBookIt = takeEvery(actions.requestOpenBookIt, async function (ctx) {
  const inTeams = selectInTeams(ctx.getState())
  const config = selectBookitSettings(ctx.getState())

  if (!config) return

  async function openBookItLocally() {
    const token = await getBookItToken()
    if (token === '') return

    const height = Math.round(window.innerHeight * .8)
    const width = Math.round(window.innerWidth * .8)
    const request = await getBookItUrl(token)

    if (request.error) {
      ctx.dispatch(actions.showClientNotification(request.error))
    } else {
      const url = request.data.url + '?otp=' + request.data.token
      try {
        window.open(url, 'bookit', 'width=' + width + ',height=' + height)
      } catch (e) {
        console.error(e)
      }
    }
  }

  if (inTeams) {
    // send request to other clients open bookit
    const response = take(ctx, actions.responseOpenBookIt)

    try {
      await requestOpenBookItWindow('request', config.clientId, config.ipAddress)
    } catch (e) {
      openBookItLocally()
    }

    const result = await Promise.race([
      response.then(() => true),
      delay(2.5 * 1000).then(() => false),
    ])

    if (!result) {
      openBookItLocally()
    }

  } else {
    openBookItLocally()
  }
})

const onEnhancedIntegration = takeEvery(actions.updateEnhancedIntegration, async function (ctx, { extension, on }) {
  const myId = selectMyself(ctx.getState())
  if (myId) {
    //save to the server
    const data = await getTeamsEnhancedCti(extension, on)

    if (extension === myId.id) {
      //update cache
      ctx.dispatch(actions.setEnhancedIntegration(data))
      //tell integration app
      IpcRenderSendClientIntegrated(data)
    }
  }
})

const onTeamsToggleDeviceGroup = takeEvery(actions.teamsToggleDeviceGroup, async function (ctx, { on, extension }) {
  const myId = selectMyself(ctx.getState())
  const data = selectExtension(ctx.getState(), extension)
  if (myId && data) {

    if (on) {
      setDeviceGroupOther(data.id)
    } else {
      setDeviceGroupOffice(data.id)
    }
  }
})

const onStartScreenRecording = takeEvery(actions.startScreenRecording, async function (ctx, { mediaId }) {

  if (!recording) {
    recording = true

    if (!streamimg) {
      streamimg = true
      /*
      const displaymediastreamconstraints = {
        video: {
            displaySurface: 'monitor', // monitor, window, application, browser
            logicalSurface: true,
            cursor: 'always' // never, always, motion
        }
      */
      // above constraints are NOT supported YET
      // that's why overriding them
      const displaymediastreamconstraints = {
        video: true,
      }

      stream = await navigator.mediaDevices.getDisplayMedia(displaymediastreamconstraints);
    }

    if (stream !== null) {

      recorder = new RecordRTC(stream, {
        type: 'video',
        mimeType: 'video/mp4',
        /*videoBitsPerSecond: 128000,
        frameInterval: 2,
        frameRate: 2,
        bufferSize: 1024,
        mimeType : 'video/mp4',*/
      });
      recorder.startRecording();

    }
  }
})

const onStopScreenRecording = takeEvery(actions.stopScreenRecording, async function (ctx, { mediaId }) {

  if (recorder !== null) {
    recorder.stopRecording(() => {
      if (recorder !== null) {
        const data = recorder.getBlob()

        screenRecordingUpload(data, mediaId)

        recorder.destroy()
        recorder = null;

        recording = false
      }
    })
  }

})

const onSocketClosed = takeEvery(actions.socketClosed, async function (ctx) {
  // need to relaod data in the back ground
  const handlers = [
    fetchConfig,
    fetchUnreadVoiceMailCount,
  ]

  ctx.dispatch(actions.forceReload())

  await all(...handlers)(ctx)

  //try to open socket again
  runPushUpdates(ctx)
})

export default all(
  onOpenBookIt,
  callControl,
  commands,
  emails,
  facebook,
  media,
  miscApi,
  onInit,
  outbound,
  pushUpdates,
  queues,
  recent,
  search,
  session,
  tasks,
  twitter,
  voiceMail,
  wrapups,
  instantmessager,

  updateNow,
  onOpenAgentView,

  extensions,
  contacts,

  onRecordGreeting,
  setExtensionPresenceExpiry,

  webrtc,
  janusExtra,
  onEnhancedIntegration,
  onTeamsToggleDeviceGroup,
  callTranscriptions,

  onStartScreenRecording,
  onStopScreenRecording,

  onSocketClosed,
)
